import { useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';

import { DataTable, type TableColumn } from '@/src/ui';
import type { StatusReportSummaryDTO } from '../models/statusReportDTOs';
import { ReportHeaderStyle, ReportCellStyle } from '@/src/utils';

type GroupBy = 'examClass' | 'exam';

interface StatusDataTableProps {
    data: Array<StatusReportSummaryDTO>;
    groupBy: GroupBy;
    onOpenDetails?: (examAssignmentId: number, classId?: number) => void;
    selectedClassId?: number;
}

// Status report table using shared DataTable
export default function StatusDataTable(props: StatusDataTableProps) {
    const { data, groupBy, selectedClassId } = props;

    const buildDetailsUrl = useCallback(
        (row: StatusReportSummaryDTO) => {
            return (
                `/reports/studentStatusReport?` +
                (row.examAssignmentId != null ? `examAssignmentId=${row.examAssignmentId}&` : '') +
                (groupBy === 'exam'
                    ? (selectedClassId != null ? `classId=${selectedClassId}&` : '') +
                      `isByExam=true&` +
                      ((row as any).examId != null ? `examId=${(row as any).examId}&` : '')
                    : row.classId != null
                      ? `classId=${row.classId}&`
                      : '') +
                (row.examName ? `examName=${encodeURIComponent(row.examName)}&` : '') +
                (row.className ? `className=${encodeURIComponent(row.className)}&` : '').replace(
                    /&$/,
                    ''
                )
            );
        },
        [groupBy, selectedClassId]
    );

    // Check if details exist
    const hasDetails = (row: StatusReportSummaryDTO) => row.examAssignmentId != null;

    // Define table columns
    const columns = useMemo<TableColumn[]>(() => {
        const numSort = (a: number, b: number, dir: 'asc' | 'desc') =>
            dir === 'asc' ? a - b : b - a;

        return [
            {
                dataKey: 'examName',
                header: 'Assessment',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: StatusReportSummaryDTO) =>
                    hasDetails(row) ? (
                        <Link to={buildDetailsUrl(row)} title="View student details">
                            {row.examName}
                        </Link>
                    ) : (
                        row.examName
                    ),
            },
            {
                dataKey: 'className',
                header: 'Class Name',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                hideColumn: groupBy === 'exam', // hide class column if grouped by exam
            },
            {
                dataKey: 'studentsAssigned',
                header: 'Students Assigned',
                align: 'center',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                sortMethod: numSort,
                formatter: (row) => String(row.studentsAssigned ?? 0),
            },
            {
                dataKey: 'studentsCompleted',
                header: 'Students Completed',
                align: 'center',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                sortMethod: numSort,
                formatter: (row) => String(row.studentsCompleted ?? 0),
            },
            {
                dataKey: 'studentsIncomplete',
                header: 'Students Incomplete',
                align: 'center',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                sortMethod: numSort,
                formatter: (row) => String(row.studentsIncomplete ?? 0),
            },
            {
                dataKey: 'studentsPassed',
                header: 'Students Passed',
                align: 'center',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                sortMethod: numSort,
                formatter: (row) => String(row.studentsPassed ?? 0),
            },
        ];
    }, [groupBy, buildDetailsUrl]);

    // Render table
    return (
        <DataTable
            data={data}
            columns={columns}
            tableName="StatusReport"
            rowsPerPageInitial={50}
            enableExportCSV
            alternatingRowColor
            defaultSortColumn="examName"
            defaultSortDirection="asc"
        />
    );
}
